import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { MapPin, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-level in-memory cache (persists across component mounts)
const _cache = new Map();
const CACHE_LIMIT = 100;

function cacheGet(key) {
    return _cache.get(key);
}
function cacheSet(key, val) {
    if (_cache.size >= CACHE_LIMIT) {
        const firstKey = _cache.keys().next().value;
        _cache.delete(firstKey);
    }
    _cache.set(key, val);
}

function highlightMatch(text, query) {
    if (!query || !text) return text;
    const terms = query.trim().split(/\s+/).filter(t => t.length >= 2);
    if (terms.length === 0) return text;
    try {
        const escaped = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
        const regex = new RegExp(`(${escaped})`, 'gi');
        const parts = text.split(regex);
        return parts.map((part, i) =>
            regex.test(part)
                ? <mark key={i} className="bg-[#C9A962]/30 text-[#1B365D] font-semibold rounded px-0.5">{part}</mark>
                : <span key={i}>{part}</span>
        );
    } catch {
        return text;
    }
}

function AddressAutocomplete({
    value,
    onChange,
    onSelect,
    placeholder = "Saisir une adresse",
    className = "",
    inputClassName = "",
    disabled = false,
    autoFocus = false,
    testId,
}){
    const [inputValue, setInputValue] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [loading, setLoading] = useState(false);

    const debounceRef = useRef(null);
    const abortRef = useRef(null);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const lastQueryRef = useRef('');

    // Keep internal state synced with external value
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowSuggestions(false);
                setActiveIndex(-1);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const runSearch = useCallback(async (query) => {
        const q = query.trim();
        if (q.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            setLoading(false);
            return;
        }
        lastQueryRef.current = q;

        // Cache hit
        const cached = cacheGet(q.toLowerCase());
        if (cached) {
            setSuggestions(cached);
            setShowSuggestions(cached.length > 0);
            setActiveIndex(-1);
            setLoading(false);
            return;
        }

        // Abort any in-flight request
        if (abortRef.current) abortRef.current.abort();
        const ctl = new AbortController();
        abortRef.current = ctl;

        setLoading(true);
        try {
            const resp = await axios.get(`${API}/geocode/search`, {
                params: { q, limit: 7 },
                signal: ctl.signal,
            });
            // Guard: only apply if still latest query
            if (lastQueryRef.current !== q) return;
            const data = Array.isArray(resp.data) ? resp.data : [];
            const items = data.map((item) => {
                const a = item.address || {};
                const short = [
                    a.house_number,
                    a.road,
                    a.postcode,
                    a.city || a.town || a.village,
                ].filter(Boolean).join(' ');
                return {
                    display: item.display_name || short,
                    short: short || item.display_name || '',
                    lat: item.lat,
                    lon: item.lon,
                    house_number: a.house_number || '',
                    road: a.road || '',
                    postcode: a.postcode || '',
                    city: a.city || a.town || a.village || '',
                };
            }).filter(it => it.short);

            cacheSet(q.toLowerCase(), items);
            setSuggestions(items);
            setShowSuggestions(true);
            setActiveIndex(-1);
        } catch (err) {
            if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
                console.error('Geocode error:', err);
            }
        } finally {
            if (lastQueryRef.current === q) setLoading(false);
        }
    }, []);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        onChange?.(val);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runSearch(val), 150);
    };

    const applySelection = (s) => {
        const picked = s.short || s.display;
        setInputValue(picked);
        onChange?.(picked);
        onSelect?.({
            adresse: picked,
            display: s.display,
            lat: s.lat,
            lon: s.lon,
            house_number: s.house_number,
            road: s.road,
            postcode: s.postcode,
            city: s.city,
        });
        setShowSuggestions(false);
        setSuggestions([]);
        setActiveIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) {
            if (e.key === 'ArrowDown' && inputValue.trim().length >= 3) {
                runSearch(inputValue);
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        } else if (e.key === 'Enter') {
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
                e.preventDefault();
                applySelection(suggestions[activeIndex]);
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setActiveIndex(-1);
        } else if (e.key === 'Tab') {
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
                applySelection(suggestions[activeIndex]);
            } else {
                setShowSuggestions(false);
            }
        }
    };

    // Keep active item scrolled into view
    useEffect(() => {
        if (activeIndex < 0 || !listRef.current) return;
        const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    const trimmedQuery = useMemo(() => (inputValue || '').trim(), [inputValue]);

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <Input
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        if (suggestions.length > 0) setShowSuggestions(true);
                    }}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    autoComplete="off"
                    spellCheck={false}
                    data-testid={testId}
                    className={`pl-9 pr-9 bg-gray-50 border-gray-200 focus:bg-white focus:ring-2 focus:ring-[#C9A962]/40 focus:border-[#C9A962] transition-all ${inputClassName}`}
                />
                {loading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9A962] animate-spin" />
                )}
            </div>

            {showSuggestions && suggestions.length > 0 && (
                <div
                    ref={listRef}
                    className="absolute z-[100] w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl max-h-72 overflow-y-auto animate-in fade-in-0 slide-in-from-top-1 duration-150"
                    role="listbox"
                >
                    {suggestions.map((s, idx) => {
                        const isActive = idx === activeIndex;
                        return (
                            <div
                                key={`${s.lat}-${s.lon}-${idx}`}
                                data-idx={idx}
                                role="option"
                                aria-selected={isActive}
                                onMouseDown={(e) => { e.preventDefault(); applySelection(s); }}
                                onMouseEnter={() => setActiveIndex(idx)}
                                data-testid={testId ? `${testId}-option-${idx}` : undefined}
                                className={`group flex items-start gap-3 px-4 py-2.5 cursor-pointer border-b border-gray-50 last:border-0 transition-colors ${
                                    isActive ? 'bg-[#C9A962]/10' : 'hover:bg-gray-50'
                                }`}
                            >
                                <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 transition-colors ${isActive ? 'text-[#C9A962]' : 'text-gray-400 group-hover:text-[#1B365D]'}`} />
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm text-[#1B365D] font-medium truncate">
                                        {highlightMatch(s.short, trimmedQuery)}
                                    </p>
                                    {s.display && s.display !== s.short && (
                                        <p className="text-xs text-gray-400 truncate">{s.display}</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showSuggestions && suggestions.length === 0 && !loading && trimmedQuery.length >= 3 && (
                <div className="absolute z-[100] w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl px-4 py-3 animate-in fade-in-0 duration-150">
                    <p className="text-sm text-gray-400">Aucune adresse trouvée</p>
                </div>
            )}
        </div>
    );
}

// Memoized export — prevents re-render when ProspectForm's other inputs change.
// Comparison is shallow on props (value, onChange, onSelect…) so callers should
// pass stable callbacks (useCallback) for maximum benefit.
export default memo(AddressAutocomplete);
